import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import { TRADING_STATUSES, tradingStatusForFlag } from '../../config/tradingStatus.js';

function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}
function statusBadge(s) {
  if (s === 'Completed') return 'bg-success';
  if (s === 'Discarded') return 'bg-secondary';
  return 'bg-warning text-dark';
}
// Convert an ISO string to the value a datetime-local input expects.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  // datetime-local shows (and returns) the user's local time, not UTC.
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

export default function OrderTriagingPage() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [draft, setDraft] = useState(null); // open parser interface
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    const res = await api.getAssistedQueue();
    setQueue(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load the processing queue.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const openDraft = async (item) => {
    setPageMsg(null);
    setFormError('');
    setBusy(true);
    try {
      const d = await api.getAssistedDraft(item.id);
      setDraft(d);
      setForm({
        vesselId: d.parsed.vesselId || '',
        portId: d.parsed.portId || '',
        berthId: d.parsed.berthId || '',
        movementType: d.parsed.movementType || 'Docking',
        tradingStatus: tradingStatusForFlag(d.options.vessels.find((v) => v.id === d.parsed.vesselId)?.flag),
        scheduledAt: toLocalInput(d.parsed.scheduledAt),
        requestedTugCount: d.parsed.requestedTugCount != null ? String(d.parsed.requestedTugCount) : '',
        pilotageRequired: true,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to open the draft.' });
    } finally {
      setBusy(false);
    }
  };

  const closeDraft = () => {
    setDraft(null);
    setForm(null);
  };

  const createBooking = async () => {
    setFormError('');
    if (!form.vesselId || !form.portId || !form.movementType || !form.tradingStatus || !form.scheduledAt) {
      setFormError('Vessel, port, movement, trading status and date/time are required.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.createAssistedBooking(draft.id, {
        vesselId: form.vesselId,
        portId: form.portId,
        berthId: form.berthId || null,
        movementType: form.movementType,
        tradingStatus: form.tradingStatus,
        // Parse the datetime-local value as local time (no 'Z'), then send UTC.
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        requestedTugCount: form.requestedTugCount ? Number(form.requestedTugCount) : null,
        pilotageRequired: form.pilotageRequired,
      });
      await load();
      setDraft(null);
      setForm(null);
      setPageMsg({ type: 'success', text: `Booking ${res.bookingRef} created from the email draft.` });
    } catch (err) {
      setFormError(err.message || 'Failed to create booking.');
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await api.discardAssistedDraft(draft.id);
      await load();
      setDraft(null);
      setForm(null);
      setPageMsg({ type: 'info', text: 'Draft discarded.' });
    } catch (err) {
      setFormError(err.message || 'Failed to discard.');
    } finally {
      setBusy(false);
    }
  };

  const berthsForPort = draft
    ? draft.options.berths.filter((b) => !form?.portId || b.port_id === form.portId)
    : [];

  return (
    <div className="container-fluid py-4" data-testid="order-triaging-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-headset me-2" aria-hidden="true" />
                Order Triaging &amp; Assisted Bookings
              </h3>
              <p className="text-muted mb-0 small">
                Handle incoming booking queues (phone/email) and verify AI-parsed draft bookings.
              </p>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="triaging-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="triaging-message">
          {pageMsg.text}
        </div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white pt-4 border-bottom-0">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-inbox me-2" aria-hidden="true" />
            Bookings Processing Queue
          </h5>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Client / Customer</th>
                    <th>Source</th>
                    <th>Received</th>
                    <th>Received Content</th>
                    <th>Status</th>
                    <th aria-label="action" />
                  </tr>
                </thead>
                <tbody>
                  {queue.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No items in the processing queue.
                      </td>
                    </tr>
                  )}
                  {queue.map((q) => (
                    <tr key={q.id} data-testid="queue-row" data-client={q.clientName} data-status={q.status}>
                      <td className="fw-bold">{q.clientName}</td>
                      <td>
                        <span className="badge bg-info text-dark">{q.source}</span>
                      </td>
                      <td className="small">{fmt(q.receivedAt)}</td>
                      <td className="small text-muted">{q.preview}</td>
                      <td>
                        <span className={`badge ${statusBadge(q.status)}`} data-testid="queue-status">
                          {q.status}
                        </span>
                      </td>
                      <td className="text-end">
                        {q.status === 'AI-Draft' && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openDraft(q)}
                            disabled={busy}
                            data-testid="process-ai-draft"
                          >
                            <i className="fas fa-robot me-1" aria-hidden="true" />
                            Process AI Draft
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Email booking parser interface */}
      {draft && form && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="parser-modal">
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-robot me-2 text-primary" aria-hidden="true" />
                    Email Booking Parser — {draft.clientName}
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeDraft} />
                </div>
                <div className="modal-body">
                  {formError && <div className="alert alert-danger py-2">{formError}</div>}
                  <div className="row">
                    <div className="col-lg-5 mb-3">
                      <label className="form-label small fw-bold text-muted">Original Email Body</label>
                      <textarea
                        className="form-control font-monospace small"
                        rows={14}
                        value={draft.rawBody}
                        readOnly
                        data-testid="email-body"
                      />
                      <p className="small text-muted mt-1" data-testid="parse-confidence">
                        <i className="fas fa-wand-magic-sparkles me-1" aria-hidden="true" />
                        AI extraction confidence: {draft.parsed.confidencePct}%
                      </p>
                    </div>
                    <div className="col-lg-7 mb-3">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-vessel" className="form-label small fw-bold text-muted">
                            Detected Vessel <span className="text-danger">*</span>
                          </label>
                          <select
                            id="d-vessel"
                            className="form-select"
                            value={form.vesselId}
                            onChange={(e) => setForm({ ...form, vesselId: e.target.value })}
                            data-testid="detected-vessel"
                          >
                            <option value="">Select…</option>
                            {draft.options.vessels.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-movement" className="form-label small fw-bold text-muted">
                            Detected Movement <span className="text-danger">*</span>
                          </label>
                          <select
                            id="d-movement"
                            className="form-select"
                            value={form.movementType}
                            onChange={(e) => setForm({ ...form, movementType: e.target.value })}
                            data-testid="detected-movement"
                          >
                            {draft.options.movementTypes.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-trading" className="form-label small fw-bold text-muted">
                            Trading Status <span className="text-danger">*</span>
                          </label>
                          <select
                            id="d-trading"
                            className="form-select"
                            value={form.tradingStatus}
                            onChange={(e) => setForm({ ...form, tradingStatus: e.target.value })}
                            data-testid="draft-trading"
                          >
                            <option value="">Select…</option>
                            {TRADING_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-port" className="form-label small fw-bold text-muted">
                            Detected Port <span className="text-danger">*</span>
                          </label>
                          <select
                            id="d-port"
                            className="form-select"
                            value={form.portId}
                            onChange={(e) => setForm({ ...form, portId: e.target.value, berthId: '' })}
                            data-testid="detected-port"
                          >
                            <option value="">Select…</option>
                            {draft.options.ports.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-berth" className="form-label small fw-bold text-muted">
                            Detected Berth
                          </label>
                          <select
                            id="d-berth"
                            className="form-select"
                            value={form.berthId}
                            onChange={(e) => setForm({ ...form, berthId: e.target.value })}
                            data-testid="detected-berth"
                          >
                            <option value="">Select…</option>
                            {berthsForPort.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-datetime" className="form-label small fw-bold text-muted">
                            Detected Date/Time <span className="text-danger">*</span>
                          </label>
                          <input
                            id="d-datetime"
                            type="datetime-local"
                            className="form-control"
                            value={form.scheduledAt}
                            onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                            data-testid="detected-datetime"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="d-tugs" className="form-label small fw-bold text-muted">
                            Recommended Tug Count
                          </label>
                          <input
                            id="d-tugs"
                            type="number"
                            min="1"
                            className="form-control"
                            value={form.requestedTugCount}
                            onChange={(e) => setForm({ ...form, requestedTugCount: e.target.value })}
                            data-testid="detected-tugs"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={discard}
                    disabled={busy}
                    data-testid="discard-draft"
                  >
                    Decline / Discard Draft
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={createBooking}
                    disabled={busy}
                    data-testid="review-create-booking"
                  >
                    {busy && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-check me-1" aria-hidden="true" />
                    Review and Create Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
